import { useState, useEffect } from 'react'
import {
  Mail,
  Linkedin,
  Twitter,
  Instagram,
  Facebook,
  MapPin,
  Phone,
} from 'lucide-react'
import { getSiteSettings, SiteSettings } from '@/services/content'
import useLanguageStore from '@/stores/languageStore'

export const Footer = () => {
  const { currentLocale } = useLanguageStore()
  const [settings, setSettings] = useState<SiteSettings | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true)
      const { data } = await getSiteSettings(currentLocale)
      if (data) setSettings(data)
      setLoading(false)
    }
    fetchSettings()
  }, [currentLocale])

  const footerText =
    settings?.footer_text ||
    (currentLocale === 'pt'
      ? 'Aplicações inteligentes à medida para negócios reais. Transformamos processos manuais em eficiência digital.'
      : 'Smart custom apps for real businesses. Transforming manual processes into digital efficiency.')
  const contactEmail = settings?.contact_email || 'geral@nucleodigital.app'

  return (
    <footer className="bg-slate-50 border-t border-slate-200 py-12">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-8 md:gap-6">
          <div className="text-center md:text-left flex flex-col items-center md:items-start">
            <div className="flex items-center gap-3 mb-4">
              {!loading && settings?.company_logo_url ? (
                <img
                  src={settings.company_logo_url}
                  alt="Núcleo Digital"
                  className="h-8 w-auto object-contain"
                />
              ) : (
                <h3 className="text-xl font-bold text-slate-900">
                  Núcleo Digital
                </h3>
              )}
            </div>
            <p className="text-slate-500 text-sm max-w-xs">{footerText}</p>
            {settings?.address && (
              <div className="flex items-start gap-2 mt-4 text-slate-500 text-sm max-w-xs text-left">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{settings.address}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            {settings?.linkedin_url && (
              <a
                href={settings.linkedin_url}
                target="_blank"
                rel="noreferrer"
                className="p-2 text-slate-400 hover:text-primary transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
            )}
            {settings?.facebook_url && (
              <a
                href={settings.facebook_url}
                target="_blank"
                rel="noreferrer"
                className="p-2 text-slate-400 hover:text-primary transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
            )}
            {settings?.instagram_url && (
              <a
                href={settings.instagram_url}
                target="_blank"
                rel="noreferrer"
                className="p-2 text-slate-400 hover:text-primary transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
            )}
            {/* Fallback for social icons if none are set */}
            {!settings?.linkedin_url &&
              !settings?.facebook_url &&
              !settings?.instagram_url && (
                <>
                  <a
                    href="#"
                    className="p-2 text-slate-400 hover:text-primary transition-colors"
                    aria-label="LinkedIn"
                  >
                    <Linkedin size={20} />
                  </a>
                  <a
                    href="#"
                    className="p-2 text-slate-400 hover:text-primary transition-colors"
                    aria-label="Twitter"
                  >
                    <Twitter size={20} />
                  </a>
                </>
              )}
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center text-sm text-slate-400 gap-4">
          <p>
            © {new Date().getFullYear()} Núcleo Digital.{' '}
            {currentLocale === 'pt'
              ? 'Todos os direitos reservados.'
              : 'All rights reserved.'}
          </p>
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
            {settings?.phone_number && (
              <div className="flex items-center gap-2">
                <Phone size={16} />
                <a
                  href={`tel:${settings.phone_number}`}
                  className="hover:text-primary transition-colors"
                >
                  {settings.phone_number}
                </a>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Mail size={16} />
              <a
                href={`mailto:${contactEmail}`}
                className="hover:text-primary transition-colors"
              >
                {contactEmail}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
